import { FileText, LayoutTemplate, Megaphone, MessageSquare } from 'lucide-vue-next'
import { type App, type Component, defineAsyncComponent } from 'vue'
import { registerComponents } from './components'
import { campaignWriteGuard } from './guards'

export { campaignHasDeliverables } from './model/card'
export { useCampaignStore } from './store'

declare module 'vue-router' {
  interface RouteMeta {
    sidePanel?: { icon: Component; labelKey: string }
    campaignCrumbKey?: string
  }
}

export const createCampaigns = () => ({
  install: (app: App) => {
    registerComponents(app)
    const router = app.getRouter()
    const campaignsCrumb = { labelKey: 'campaigns.title', to: { name: 'campaigns' } }

    router.addRoute('organization', {
      path: 'campaigns',
      name: 'campaigns',
      meta: {
        head: { titleKey: 'campaigns.title' },
        nav: {
          icon: Megaphone,
          order: 11,
          section: 'nav.workspace',
          below: defineAsyncComponent(() => import('./components/CampaignNavRecent.vue'))
        },
        crumbs: [{ labelKey: 'nav.workspace', to: { name: 'dashboard' } }, { labelKey: 'campaigns.title' }]
      },
      component: () => import('./pages/CampaignsPage.vue')
    })

    router.addRoute('organization', {
      path: 'campaigns/:campaignId/discovery',
      name: 'campaign-discovery',
      beforeEnter: campaignWriteGuard,
      meta: {
        head: { titleKey: 'campaigns.new' },
        crumbs: [campaignsCrumb, { labelKey: 'campaigns.new' }],
        sidePanel: { icon: FileText, labelKey: 'campaigns.brief.soFar' }
      },
      components: {
        default: () => import('./pages/CampaignDiscoveryPage.vue'),
        'header-actions': () => import('./components/CampaignPanelToggle.vue')
      }
    })

    router.addRoute('organization', {
      path: 'campaigns/:campaignId/discovery/concepts',
      name: 'campaign-concepts',
      beforeEnter: campaignWriteGuard,
      meta: {
        head: { titleKey: 'campaigns.concepts.title' },
        crumbs: [campaignsCrumb, { labelKey: 'campaigns.new' }, { labelKey: 'campaigns.concepts.title' }],
        sidePanel: { icon: LayoutTemplate, labelKey: 'campaigns.concepts.soFar' }
      },
      components: {
        default: () => import('./pages/CampaignConceptsPage.vue'),
        'header-actions': () => import('./components/CampaignPanelToggle.vue')
      }
    })

    router.addRoute('organization', {
      path: 'campaigns/:campaignId/concepts/chat/:conversationId?',
      name: 'campaign-concepts-chat',
      beforeEnter: campaignWriteGuard,
      meta: {
        head: { titleKey: 'campaigns.conceptsChat.title' },
        crumbs: [campaignsCrumb, { labelKey: 'campaigns.conceptsChat.title' }],
        sidePanel: { icon: MessageSquare, labelKey: 'campaigns.conceptsChat.chats' }
      },
      components: {
        default: () => import('./pages/CampaignConceptsChatPage.vue'),
        'header-actions': () => import('./components/CampaignPanelToggle.vue')
      }
    })

    const campaignHeader = { header: () => import('./components/CampaignHeader.vue') }

    router.addRoute('organization', {
      path: 'campaigns/:campaignId',
      name: 'campaign',
      meta: { head: { titleKey: 'campaigns.title' } },
      components: { default: () => import('./pages/CampaignPage.vue'), ...campaignHeader }
    })

    router.addRoute('organization', {
      path: 'campaigns/:campaignId/brief',
      name: 'campaign-brief',
      meta: { head: { titleKey: 'campaigns.brief.title' }, campaignCrumbKey: 'campaigns.brief.title' },
      components: { default: () => import('./pages/CampaignBriefPage.vue'), ...campaignHeader }
    })

    router.addRoute('organization', {
      path: 'campaigns/:campaignId/concepts',
      name: 'campaign-concept-list',
      meta: { head: { titleKey: 'campaigns.concepts.title' }, campaignCrumbKey: 'campaigns.concepts.title' },
      components: { default: () => import('./pages/CampaignConceptListPage.vue'), ...campaignHeader }
    })

    router.addRoute('organization', {
      path: 'campaigns/:campaignId/:tab(deliverables|memory)',
      redirect: to => ({ name: 'campaign', params: { organizationId: to.params.organizationId, campaignId: to.params.campaignId } })
    })
  }
})
